import { FlexCol } from "@design-system/Layout/Flex";
import {
    Table,
    usePaginatedSelection,
    usePagination,
    useSorting,
} from "@design-system/Table";
import { useDebounce } from "@uidotdev/usehooks";
import { useEffect, type FC } from "react";
import { useQueryParam } from "use-query-params";
import { activeFilterParamArgs } from "../../ctx";
import { useSuppliersList, useSuppliersMinimal } from "../../data";
import { type SupplierSortKey } from "../../types";
import { ActionBar } from "./ActionBar";
import { useColumns } from "./columns";

export const SupplierTable: FC = () => {
    const columns = useColumns();

    const { pagination, take, skip } = usePagination();
    const { sorting, field, order } = useSorting<SupplierSortKey>(
        "createdAt",
        "desc",
    );

    const [statusFilter] = useQueryParam(...activeFilterParamArgs.statusFilter);
    const [tagFilter] = useQueryParam(...activeFilterParamArgs.tagFilter);
    const [searchFilter] = useQueryParam(...activeFilterParamArgs.searchFilter);
    const [questionnaireFilter] = useQueryParam(
        ...activeFilterParamArgs.questionnaireFilter,
    );
    const [progressFilterMin] = useQueryParam(
        ...activeFilterParamArgs.progressFilter.min,
    );
    const [progressFilterMax] = useQueryParam(
        ...activeFilterParamArgs.progressFilter.max,
    );
    const [noQuestionnaireOnly] = useQueryParam(
        ...activeFilterParamArgs.noQuestionnaireOnly,
    );

    const debouncedMin = useDebounce(progressFilterMin, 1000);
    const debouncedMax = useDebounce(progressFilterMax, 1000);

    const { suppliers, isPending } = useSuppliersList(
        {
            search: searchFilter,
            tags: tagFilter?.filter(Boolean) as string[],
            lastQuestionnaireStatus: statusFilter,
            noQuestionnaireOnly,
            lastQuestionnaireNames: questionnaireFilter?.filter(
                Boolean,
            ) as string[],
            lastQuestionnaireProgress: {
                min: Number.isNaN(debouncedMin) ? 0 : debouncedMin,
                max: Number.isNaN(debouncedMax) ? 100 : debouncedMax,
            },
        },
        {
            skip,
            take,
        },
        {
            [field]: order,
        } as const,
    );

    const {
        suppliers: suppliersMinimal,
        isPending: isSuppliersMinimalPending,
    } = useSuppliersMinimal(
        {
            search: searchFilter,
            tags: tagFilter?.filter(Boolean) as string[],
            lastQuestionnaireStatus: statusFilter,
            noQuestionnaireOnly,
            lastQuestionnaireNames: questionnaireFilter?.filter(
                Boolean,
            ) as string[],
            lastQuestionnaireProgress: {
                min: Number.isNaN(debouncedMin) ? 0 : debouncedMin,
                max: Number.isNaN(debouncedMax) ? 100 : debouncedMax,
            },
        },
        {
            [field]: order,
        } as const,
    );

    const { paginatedSelection, resetSelection } = usePaginatedSelection(
        suppliers?.list,
    );

    useEffect(() => {
        pagination.onPaginationChange({
            pageIndex: 0,
            pageSize: pagination.state.pageSize,
        });
        resetSelection();
    }, [
        searchFilter,
        tagFilter,
        statusFilter,
        questionnaireFilter,
        debouncedMin,
        debouncedMax,
        noQuestionnaireOnly,
    ]);

    return (
        <FlexCol w="full">
            <Table
                columns={columns}
                data={suppliers?.list ?? []}
                pagination={pagination}
                loading={isPending}
                sorting={sorting}
                paginatedSelection={paginatedSelection}
                rowCount={suppliers?.count}
            />
            <ActionBar
                unset={resetSelection}
                paginatedSelectedRows={paginatedSelection.paginatedSelectedRows}
                rowCount={suppliers?.count}
                suppliers={suppliersMinimal ?? []}
                isSuppliersPending={isSuppliersMinimalPending}
            />
        </FlexCol>
    );
};
